import { lightTheme } from './light';
import { darkTheme } from './dark';
import { dimTheme } from './dim';
import { autumnDayTheme } from './autumn-day';
import { rosePineMoonTheme } from './rose-pine';
import { rosePineDawnTheme } from './rose-pine-dawn';
import { catppuccinMochaTheme } from './catppuccin-mocha';
import { tempusDayTheme } from './tempus-day';

export const themes = {
  'autumn-day': autumnDayTheme,
  light: lightTheme,
  dark: darkTheme,
  dim: dimTheme,
  'rose-pine-moon': rosePineMoonTheme,
  'rose-pine-dawn': rosePineDawnTheme,
  catppuccin: catppuccinMochaTheme,
  'tempus-day': tempusDayTheme
};

export const defaultTheme = 'autumn-day';

export function getTheme(themeName) {
  return themes[themeName] || themes[defaultTheme];
}

/**
 * Format a theme id for display: "rose-pine-moon" -> "Rose Pine Moon"
 * @param {string} themeName - Theme id (kebab-case)
 * @returns {string} Display name
 */
export function formatThemeName(themeName) {
  return themeName
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Convert OKLCH color string to hex for meta tags
 * @param {string} oklch - OKLCH color string (e.g., "95% 0 0")
 * @returns {string} Hex color string (e.g., "#f2f2f2")
 */
function oklchToHex(oklch) {
  try {
    // Create a temporary element to use browser's color conversion
    const temp = document.createElement('div');
    temp.style.color = `oklch(${oklch})`;
    document.body.appendChild(temp);

    // Get computed color (browser converts to RGB)
    const computed = getComputedStyle(temp).color;
    document.body.removeChild(temp);

    // Parse RGB values
    const match = computed.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
    if (!match) return '#ffffff';

    // Convert to hex
    const r = parseInt(match[1]).toString(16).padStart(2, '0');
    const g = parseInt(match[2]).toString(16).padStart(2, '0');
    const b = parseInt(match[3]).toString(16).padStart(2, '0');

    return `#${r}${g}${b}`;
  } catch (e) {
    console.warn('Failed to convert OKLCH to hex:', e);
    return '#ffffff';
  }
}

export function applyTheme(theme) {
  const root = document.documentElement;

  // Apply color scheme
  root.setAttribute('data-theme', theme.name);
  root.style.colorScheme = theme.colorScheme;

  // Apply color variables
  Object.entries(theme.colors).forEach(([key, value]) => {
    // Direct mapping - no conversion needed
    const cssVarName = `--color-${key}`;
    root.style.setProperty(cssVarName, value);
  });

  // Apply dimension variables
  Object.entries(theme.dimensions).forEach(([key, value]) => {
    const cssVarName = `--${key}`;
    root.style.setProperty(cssVarName, value);
  });

  // Update theme-color meta tag for mobile browsers (PWA)
  const themeColorMeta = document.getElementById('theme-color-meta');
  if (themeColorMeta && theme.colors['base-100']) {
    const hexColor = oklchToHex(theme.colors['base-100']);
    themeColorMeta.setAttribute('content', hexColor);
  }

  // Update iOS status bar style based on theme
  const appleStatusBarMeta = document.getElementById('apple-status-bar-meta');
  if (appleStatusBarMeta) {
    // Use 'black' for dark themes, 'default' for light themes
    const isDark = theme.prefersDark || theme.colorScheme === 'dark';
    appleStatusBarMeta.setAttribute('content', isDark ? 'black' : 'default');
  }
}
